import { useState } from "react";
import { useJobStore } from "@/dentlab/src/store/Jobs";
import { showNotification } from "@/dentlab/src/store/Central/selectors";
import { sendSharedFileEmail } from "@/lib/email";
import { JobDocumentWithFiles } from "@/dentlab/src/store/Jobs/job-documents.store";
import { JobItemEntityType } from "@/lib/supabase/supabaseTypes";
import { PdfTemplateType } from "@/dentlab/src/pdf-templates/document-template.types";
import { JobDocumentTypeEnum } from "../job-document.types";
import { getDefaultTemplateTypeForDocument } from "./job-document-utils";
import { Logger } from "@/lib/logger/Logger";
import { HandleExportDocumentResult } from "@/dentlab/src/hooks/useHandleExportDocument/useHandleExportDocument";
import { useCentralStore } from "@/dentlab/src/store/Central";
import { buildShareLink } from "@/lib/utils/build-share-link";
import { environment } from "@/dentlab/src/lib/utils/environment";

interface UseEmailCheckActionProps {
	jobDocument: JobDocumentWithFiles;
	jobItems: JobItemEntityType[];
	handleExportDocument: (
		templateType: PdfTemplateType,
		jobItems: JobItemEntityType[],
		jobDocument: JobDocumentWithFiles
	) => Promise<HandleExportDocumentResult>;
}

export const useEmailCheckAction = ({
	jobDocument,
	jobItems,
	handleExportDocument,
}: UseEmailCheckActionProps) => {
	const [emailActionLoading, setEmailActionLoading] = useState(false);
	const [showConnectDialog, setShowConnectDialog] = useState(false);

	const {
		connectedOrganizationEmail,
		setConnectedOrganizationEmail,
		organizationId,
		connectedOrganizationType,
		client,
	} = useCentralStore((state) => ({
		connectedOrganizationEmail: state.connectedOrganizationEmail,
		setConnectedOrganizationEmail: state.setConnectedOrganizationEmail,
		organizationId: state.organization?.id ?? "",
		connectedOrganizationType: state.connectedOrganizationType,
		client: state.client,
	}));

	const { shareFile, fetchJobDocument } = useJobStore((state) => ({
		shareFile: state.shareFile,
		fetchJobDocument: state.fetchJobDocument,
	}));

	const executeExportShareEmail = async (
		overrideConnectedOrganizationEmail?: string
	) => {
		try {
			const email =
				overrideConnectedOrganizationEmail ||
				connectedOrganizationEmail;
			if (!email) {
				throw new Error(
					"Keine E-Mail-Adresse für den verbundenen Partner gefunden"
				);
			}

			const templateType = getDefaultTemplateTypeForDocument(
				jobDocument.type as JobDocumentTypeEnum
			);
			if (!templateType) {
				showNotification({
					message: "Dieser Dokumenttyp kann nicht exportiert werden",
					type: "error",
				});
				return;
			}

			showNotification({
				message: "Dokument wird exportiert...",
				type: "info",
			});

			const result = await handleExportDocument(
				templateType,
				jobItems,
				jobDocument
			);

			if (!result || !result.success) {
				throw new Error(result?.error || "Export fehlgeschlagen");
			}

			const newFileId = result.fileId;
			if (!newFileId) {
				throw new Error("Exportierte Datei nicht gefunden");
			}

			const updatedJobDocument = await fetchJobDocument(jobDocument.id);
			const exportedFile = updatedJobDocument?.files_with_shares.find(
				(file) => file.id === newFileId
			);

			showNotification({
				message: "Datei wird geteilt...",
				type: "info",
			});

			await shareFile(newFileId, jobDocument.id);

			const shouldSendEmail = client?.is_send_email_on_share !== false;

			if (shouldSendEmail) {
				await sendSharedFileEmail({
					recipientEmail: email,
					fileName: exportedFile?.file_name || "Dokument",
					link: buildShareLink({
						fileId: newFileId,
						recipientEmail: email,
						senderOrgId: organizationId,
						recipientOrgType: connectedOrganizationType,
						dentconnectUrl: environment.VITE_DENTCONNECT_URL,
						jobId: jobDocument.job_id,
					}),
					message: "Ihr Dokument steht zum sicheren Download bereit.",
				});
				Logger.log(
					"Dokument erfolgreich exportiert und per E-Mail versendet"
				);
			} else {
				showNotification({
					message: "Dokument exportiert und geteilt",
					type: "success",
				});
				Logger.log(
					"Dokument erfolgreich exportiert und geteilt (ohne E-Mail)"
				);
			}
		} catch (error) {
			showNotification({
				message: "Fehler beim Exportieren und Teilen",
				description:
					error instanceof Error
						? error.message
						: "Ein unbekannter Fehler ist aufgetreten",
				type: "error",
			});
		}
	};

	const handleEmailCheckClick = async () => {
		if (!connectedOrganizationEmail) {
			setShowConnectDialog(true);
			return;
		}

		setEmailActionLoading(true);
		await executeExportShareEmail();
		setEmailActionLoading(false);
	};

	const handleConnectionSuccess = async (email: string) => {
		setConnectedOrganizationEmail(email);
		setShowConnectDialog(false);
		// After successful connection, execute the export/share/email flow
		setEmailActionLoading(true);
		await executeExportShareEmail(email);
		setEmailActionLoading(false);
	};

	return {
		emailActionLoading,
		showConnectDialog,
		setShowConnectDialog,
		handleEmailCheckClick,
		handleConnectionSuccess,
		connectedOrganizationEmail,
	};
};
